import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Section, Container, SectionTitle } from '../styles/StyledComponents';

const EHRContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const EHRGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  width: 100%;
  margin-top: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const EHRCard = styled(motion.div)`
  background-color: var(--white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const EHRLogo = styled.div`
  width: 150px;
  height: 80px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const EHRName = styled.h3`
  font-size: 1.3rem;
  color: var(--dark-blue);
  margin-bottom: 0.75rem;
`;

const EHRDescription = styled.p`
  color: var(--medium-gray);
  font-size: 0.95rem;
  line-height: 1.6;
`;

const EHRIntro = styled(motion.div)`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  
  p {
    color: var(--medium-gray);
    line-height: 1.8;
    font-size: 1.1rem;
  }
`;

const EHRPlatforms: React.FC = () => {
  const ehrPlatforms = [
    {
      name: "Epic",
      description: "Comprehensive EHR system used in large healthcare organizations, with modules for clinical, access and revenue, patient engagement, and population health management."
    },
    {
      name: "Cerner",
      description: "Integrated EHR platform offering clinical, financial, and operational solutions for healthcare organizations of all sizes."
    },
    {
      name: "NextGen Healthcare",
      description: "Ambulatory-focused EHR system with integrated practice management, patient engagement, and population health management capabilities."
    },
    {
      name: "Athenahealth",
      description: "Cloud-based EHR and practice management solution with built-in billing services and patient engagement tools."
    },
    {
      name: "eClinicalWorks",
      description: "Comprehensive EHR solution for practices of all sizes, featuring practice management, patient engagement, and population health tools."
    },
    {
      name: "Kareo",
      description: "Cloud-based clinical and business management platform designed specifically for independent practices."
    },
    {
      name: "DrChrono",
      description: "Mobile-friendly EHR platform with customizable medical forms, e-prescribing, and billing capabilities."
    },
    {
      name: "CareCloud",
      description: "Integrated EHR and practice management solution with advanced analytics and financial management tools."
    },
    {
      name: "Oracle Health",
      description: "Enterprise-grade healthcare solutions offering clinical, financial, and operational management capabilities."
    }
  ];

  return (
    <Section id="ehr-platforms">
      <EHRContainer>
        <SectionTitle
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          EHR/EMR Platforms
        </SectionTitle>
        
        <EHRIntro
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p>
            With extensive experience across multiple Electronic Health Record (EHR) and Electronic Medical Record (EMR) 
            platforms, I bring versatility and adaptability to healthcare documentation and workflow management. 
            My proficiency spans leading systems used in various healthcare settings, from large hospital networks 
            to independent practices.
          </p>
        </EHRIntro>
        
        <EHRGrid>
          {ehrPlatforms.map((platform, index) => (
            <EHRCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <EHRLogo>
                {/* Placeholder for logo - in a real implementation, you would have actual logos */}
                <div style={{ 
                  width: '100%', 
                  height: '50px', 
                  backgroundColor: 'var(--light-blue)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  borderRadius: '4px',
                  color: 'var(--primary-blue)',
                  fontWeight: 'bold'
                }}>
                  {platform.name}
                </div>
              </EHRLogo>
              <EHRName>{platform.name}</EHRName>
              <EHRDescription>{platform.description}</EHRDescription>
            </EHRCard>
          ))}
        </EHRGrid>
      </EHRContainer>
    </Section>
  );
};

export default EHRPlatforms;
